import { create } from 'zustand';
import {
  canEdit as canEditObject,
  canView as canViewObject,
  isFieldHidden,
  ROLE_LABELS,
  ROLES_IMPLIED_BY,
  type PermissionObject,
  type SessionPayload,
  type SessionVenture,
} from '@nest-recruit/shared';

// Who is signed in, and what they are looking at.
//
// Client state only, per CLAUDE.md §11. The payload itself is server state and
// lives in TanStack Query (`useSessionQuery`); this store holds the one genuinely
// client-side thing — which venture the viewer has scoped themselves to — plus a
// mirror of the payload so that non-React code and deeply nested components can
// read it without threading it through.
//
// S2's six mock users are gone. Everything that read `useSession().user` now reads
// a real session, and the shape barely moved: the important change is that screens
// decide from `permissions`, not from role names, because an HR Lead can clone a
// role and a browser reasoning from the name would get it wrong (F2.08).

export type Venture = SessionVenture;

/**
 * F20.05 — the scope switch, and S5's "clicking a tree node filters the ENTIRE
 * application to that unit".
 *
 * S2 built this as `all | venture`, which was right until the org tree gave the
 * viewer two more levels to click. It is one mechanism rather than three: the org
 * tree, the utility-bar switcher and every list read the same value, so a filter
 * set in one place is the filter everywhere.
 *
 * **This is a view filter, never a permission.** The server re-derives what the
 * viewer may actually see on every request; this only ever narrows further. A scope
 * pointing somewhere they cannot see returns nothing, which is the safe direction.
 */
export type Scope =
  | { readonly kind: 'all' }
  | { readonly kind: 'venture'; readonly id: string; readonly label?: string }
  | { readonly kind: 'department'; readonly id: string; readonly label?: string }
  | { readonly kind: 'team'; readonly id: string; readonly label?: string };

export const ALL_SCOPE: Scope = { kind: 'all' };

/**
 * The scope as query parameters, for any list endpoint that takes an org scope.
 *
 * Every S5-aware list spreads this into its query. Returning `{}` for "all" is what
 * makes an unscoped call the natural default rather than something to remember.
 */
export function scopeQuery(scope: Scope): {
  ventureId?: string;
  departmentId?: string;
  teamId?: string;
} {
  switch (scope.kind) {
    case 'venture':
      return { ventureId: scope.id };
    case 'department':
      return { departmentId: scope.id };
    case 'team':
      return { teamId: scope.id };
    default:
      return {};
  }
}

const SCOPE_STORAGE_KEY = 'nr.scope';

interface SessionState {
  /** Null until `/auth/me` answers, and again after signing out. */
  readonly session: SessionPayload | null;
  readonly scope: Scope;
  setSession(session: SessionPayload | null): void;
  setScope(scope: Scope): void;
}

export const useSession = create<SessionState>((set) => ({
  session: null,
  scope: readStoredScope(),
  setSession: (session) =>
    set((state) => {
      if (!session) return { session: null, scope: ALL_SCOPE };
      // A scope pointing at a venture this person can no longer see would filter
      // every list by something invisible, so it resets rather than persisting.
      // Only a venture scope can be checked against the session payload — it is the
      // only level `/auth/me` carries. A department or team scope is validated by
      // the server on the next list call, which returns nothing rather than
      // everything if it is wrong. Failing closed is the whole point.
      const current = state.scope;
      const stillValid =
        current.kind !== 'venture' || session.ventures.some((venture) => venture.id === current.id);
      return { session, scope: stillValid ? state.scope : ALL_SCOPE };
    }),
  setScope: (scope) => {
    writeStoredScope(scope);
    set({ scope });
  },
}));

/**
 * The signed-in viewer, or null.
 *
 * Components inside the shell can assume non-null — the shell does not render
 * until the session has resolved — but the type stays honest so nothing forgets
 * that the login screen exists.
 */
export function useCurrentSession(): SessionPayload | null {
  return useSession((state) => state.session);
}

/** Inside the shell, where a null session is impossible by construction. */
export function useRequiredSession(): SessionPayload {
  const session = useSession((state) => state.session);
  if (!session) throw new Error('useRequiredSession was called outside a signed-in tree');
  return session;
}

// --- Permission helpers, for screens ----------------------------------------

/** Whether the viewer may see an object at all. Default deny. */
export function useCanView(object: PermissionObject): boolean {
  const session = useCurrentSession();
  return session ? canViewObject(session, object) : false;
}

/** Whether the viewer may change an object. Always false while impersonating. */
export function useCanEdit(object: PermissionObject): boolean {
  const session = useCurrentSession();
  return session ? canEditObject(session, object) : false;
}

/** Whether a §L23 field path is masked for this viewer — render an em dash if so. */
export function useIsFieldHidden(field: string): boolean {
  const session = useCurrentSession();
  return session ? isFieldHidden(session, field) : true;
}

// --- Scope ------------------------------------------------------------------

/** The venture the current scope names, or null when it names something else. */
export function scopedVenture(session: SessionPayload, scope: Scope): Venture | null {
  if (scope.kind !== 'venture') return null;
  return session.ventures.find((v) => v.id === scope.id) ?? null;
}

/**
 * What the scope indicator says. A venture is named from the session payload so it
 * survives a reload with no org-tree fetch; a department or team carries its own
 * label, set when the node was clicked, because nothing else on the client knows it.
 */
export function scopeLabel(session: SessionPayload, scope: Scope): string {
  if (scope.kind === 'all') return 'All ventures';
  if (scope.kind === 'venture') return scopedVenture(session, scope)?.name ?? 'One venture';
  return scope.label ?? (scope.kind === 'team' ? 'One team' : 'One department');
}

/**
 * Whether the scope indicator must be showing. 06-qol §8: acting on the wrong
 * venture's data is the highest-cost navigation bug in this system, so anything
 * other than the default gets a persistent, unmissable marker.
 */
export function isScopeNonDefault(scope: Scope): boolean {
  return scope.kind !== 'all';
}

export function roleLabelsOf(session: SessionPayload): string {
  // ADR 0073: an Admin is also HR behind the scenes; show only the role actually given.
  const implied = new Set(session.roles.flatMap((role) => ROLES_IMPLIED_BY[role] ?? []));
  return session.roles
    .filter((role) => !implied.has(role))
    .map((role) => ROLE_LABELS[role])
    .join(' · ');
}

// The scope survives a reload because it is a view preference, not a permission —
// the server re-derives what the viewer may actually see on every request.
function readStoredScope(): Scope {
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    if (!raw) return ALL_SCOPE;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredScope(parsed)) return ALL_SCOPE;
    return parsed.label === undefined
      ? { kind: parsed.kind, id: parsed.id }
      : { kind: parsed.kind, id: parsed.id, label: parsed.label };
  } catch {
    return ALL_SCOPE;
  }
}

/** A type guard rather than a cast: the value came out of browser storage, which
 *  anybody can edit, so it is untrusted input like a query string. */
function isStoredScope(
  value: unknown,
): value is { kind: 'venture' | 'department' | 'team'; id: string; label?: string } {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  if (record.kind !== 'venture' && record.kind !== 'department' && record.kind !== 'team') {
    return false;
  }
  if (typeof record.id !== 'string') return false;
  return record.label === undefined || typeof record.label === 'string';
}

function writeStoredScope(scope: Scope): void {
  try {
    if (scope.kind === 'all') window.localStorage.removeItem(SCOPE_STORAGE_KEY);
    else window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // A private window with storage disabled is not a reason to fail a scope switch.
  }
}
